import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Input, Button, Checkbox, App as AntdApp } from 'antd';
import { Lock, User, Phone, MapPin } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { authService, LoginRequest } from '@/services/authService';
import { useAuthStore } from '@/store/authStore';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useSystemSettings } from '@/contexts/SystemSettingsContext';
import { systemSettingsService } from '@/services/systemSettingsService';
import { getPronounCapitalized } from '@/utils/genderHelper';  // V1.0.48

const REMEMBER_KEY = 'hv-taskflow-last-username';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();
  const { message } = AntdApp.useApp();
  const { settings, hasLogo, logoVersion } = useSystemSettings();
  const [form] = Form.useForm();
  const { isMobile, isTablet } = useBreakpoint();
  const showBrandColumn = !isMobile && !isTablet;

  // Lấy username đã lưu trong localStorage (chứ không hard-code 'anhpn')
  const [rememberedUsername, setRememberedUsername] = useState<string>('');
  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) setRememberedUsername(saved);
    } catch {}
  }, []);

  const [remember, setRemember] = useState(true);

  const from = (location.state as { from?: Location })?.from?.pathname ?? '/dashboard';

  const loginMutation = useMutation({
    mutationFn: (payload: LoginRequest) => authService.login(payload),
    onSuccess: (data, variables) => {
      // Save username (không lưu password!)
      try {
        if (remember) {
          localStorage.setItem(REMEMBER_KEY, variables.username);
        } else {
          localStorage.removeItem(REMEMBER_KEY);
        }
      } catch {}
      login(data.user, data.token);
      // V1.0.50: Xưng hô trung tính "Bạn" - không phân biệt giới tính
      message.success(`Xin chào ${getPronounCapitalized(data.user.gender)} ${data.user.fullName}!`);
      navigate(from, { replace: true });
    },
    onError: (err: Error) => {
      message.error(err.message || 'Đăng nhập thất bại');
    },
  });

  const handleSubmit = (values: { username: string; password: string }) => {
    loginMutation.mutate({
      // Lowercase + trim username để bỏ qua HOA/thường + khoảng trắng đầu/cuối
      username: values.username.trim().toLowerCase(),
      password: values.password,
    });
  };

  // Pull values từ system settings (có default fallback)
  const companyName = settings.company_name ?? 'Bệnh viện Hưng Việt';
  const companyShort = settings.company_short_name ?? 'HV';
  const systemName = settings.system_name ?? 'HV-TaskFlow';
  const loginTitle = settings.login_title ?? 'ĐĂNG NHẬP HỆ THỐNG';
  const loginDescription = settings.login_description
    ?? 'Nhập tên đăng nhập và mật khẩu được cấp bởi quản trị viên.';
  const brandSubtitle = settings.login_brand_subtitle ?? 'Cổng thông tin nội bộ';
  const brandCaption = settings.login_brand_caption ?? 'Quản lý công việc & ký điện tử';
  const companyAddress = settings.company_address ?? '34 Đại Cồ Việt, Hai Bà Trưng, Hà Nội';
  const companyHotline = settings.company_hotline ?? '(024) 3974 7777';
  const copyrightText = settings.copyright_text
    ?? `Copyright © ${new Date().getFullYear()} ${companyName}`;
  const primaryDark = settings.primary_color_dark ?? '#0F4C81';
  const primaryColor = settings.primary_color ?? '#1976D2';

  // Respect logo_display_mode: 'text' → fallback hiển thị chữ viết tắt thay vì PNG có thể lỗi
  const useTextLogo = settings.logo_display_mode === 'text';
  const logoUrl = !useTextLogo && hasLogo
    ? `${systemSettingsService.logoUrl()}?v=${logoVersion}`
    : null;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: '#F5F8FB' }}>
      {/* ============================================
          LEFT — Brand panel (desktop only)
          ============================================ */}
      {showBrandColumn && (
        <div
          style={{
            flex: 1,
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
            padding: '60px 80px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background decorative circles */}
          <div style={{
            position: 'absolute', top: -100, left: -100, width: 320, height: 320,
            borderRadius: '50%', background: 'rgba(255,255,255,0.05)',
          }} />
          <div style={{
            position: 'absolute', bottom: -60, right: -60, width: 240, height: 240,
            borderRadius: '50%', background: 'rgba(255,255,255,0.06)',
          }} />

          {/* Logo */}
          <div style={{
            width: 160, height: 160, borderRadius: '50%',
            background: '#ffffff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            marginBottom: 32,
            zIndex: 1,
          }}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="logo"
                style={{ width: '78%', height: '78%', objectFit: 'contain' }}
              />
            ) : (
              <span style={{
                fontSize: 56, fontWeight: 800, color: primaryDark,
                fontFamily: 'system-ui, -apple-system, sans-serif',
              }}>
                {companyShort}
              </span>
            )}
          </div>

          {/* Tên đơn vị */}
          <div style={{
            fontSize: 24, fontWeight: 700, letterSpacing: 2,
            textTransform: 'uppercase', textAlign: 'center', marginBottom: 8,
            zIndex: 1,
          }}>
            {companyName}
          </div>

          {/* Subtitle */}
          <div style={{
            fontSize: 13, color: 'rgba(255,255,255,0.75)',
            textAlign: 'center', letterSpacing: 1.5, fontWeight: 500,
            textTransform: 'uppercase', marginBottom: 28,
            zIndex: 1,
          }}>
            {brandSubtitle}
          </div>

          {/* Divider line */}
          <div style={{
            width: 56, height: 2, background: 'rgba(255,255,255,0.5)',
            marginBottom: 24, zIndex: 1,
          }} />

          {/* Caption / Mô tả */}
          <div style={{
            fontSize: 14, color: 'rgba(255,255,255,0.85)',
            textAlign: 'center', maxWidth: 360, lineHeight: 1.6,
            zIndex: 1,
          }}>
            {brandCaption}
          </div>

          {/* Footer info */}
          <div style={{
            position: 'absolute', bottom: 40, left: 60, right: 60,
            color: 'rgba(255,255,255,0.7)', fontSize: 12,
            textAlign: 'center', lineHeight: 1.8,
            zIndex: 1,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <MapPin size={12} />
              <span>{companyAddress}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 4 }}>
              <Phone size={12} />
              <span>Hotline: {companyHotline}</span>
            </div>
          </div>
        </div>
      )}

      {/* ============================================
          RIGHT — Login form
          ============================================ */}
      <div
        style={{
          flex: showBrandColumn ? '0 0 540px' : 1,
          padding: isMobile ? '32px 20px' : '60px 80px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: '#fff',
        }}
      >
        {/* Mobile/Tablet header với logo nhỏ */}
        {!showBrandColumn && (
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{
              width: 80, height: 80, borderRadius: '50%',
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
              marginBottom: 16,
              padding: 4,
            }}>
              <div style={{
                width: '100%', height: '100%', borderRadius: '50%', background: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
              }}>
                {logoUrl ? (
                  <img src={logoUrl} alt="logo"
                       style={{ width: '78%', height: '78%', objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontSize: 24, fontWeight: 800, color: primaryDark }}>
                    {companyShort}
                  </span>
                )}
              </div>
            </div>
            <div style={{
              fontSize: 18, fontWeight: 700, color: primaryDark,
              letterSpacing: 1, textTransform: 'uppercase',
            }}>
              {companyName}
            </div>
            <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
              {brandSubtitle}
            </div>
          </div>
        )}

        <div style={{ maxWidth: 380, width: '100%', margin: '0 auto' }}>
          {/* Logo căn giữa phía trên form (cả desktop) */}
          {showBrandColumn && (
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{
                width: 84, height: 84, borderRadius: '50%',
                background: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 16px ${primaryColor}33`,
                border: `2px solid ${primaryColor}22`,
                overflow: 'hidden',
              }}>
                {logoUrl ? (
                  <img src={logoUrl} alt="logo"
                       style={{ width: '78%', height: '78%', objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontSize: 30, fontWeight: 800, color: primaryDark }}>
                    {companyShort}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Title */}
          <h1 style={{
            fontSize: 26, fontWeight: 700, color: primaryDark,
            margin: '0 0 8px 0', textAlign: 'center', letterSpacing: 0.5,
          }}>
            {loginTitle}
          </h1>
          <p style={{
            fontSize: 13, color: '#7d8a98', textAlign: 'center',
            margin: '0 0 32px 0', lineHeight: 1.6,
          }}>
            {loginDescription}
          </p>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="on"
            requiredMark={false}
            initialValues={{
              username: rememberedUsername,
            }}
          >
            <Form.Item
              label={<span style={{ fontWeight: 600, color: '#2A3547' }}>Tên đăng nhập</span>}
              name="username"
              rules={[{ required: true, message: 'Vui lòng nhập tên đăng nhập' }]}
            >
              <Input
                size="large"
                prefix={<User size={16} style={{ color: '#7d8a98' }} />}
                placeholder="VD: anhpn1, lethib..."
                autoComplete="username"
                autoFocus={!rememberedUsername}
              />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontWeight: 600, color: '#2A3547' }}>Mật khẩu</span>}
              name="password"
              rules={[{ required: true, message: 'Vui lòng nhập mật khẩu' }]}
            >
              <Input.Password
                size="large"
                prefix={<Lock size={16} style={{ color: '#7d8a98' }} />}
                placeholder="Nhập mật khẩu"
                autoComplete="current-password"
                autoFocus={!!rememberedUsername}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)}>
                  <span style={{ fontSize: 13 }}>Ghi nhớ tên đăng nhập</span>
                </Checkbox>
                <a
                  href="#forgot"
                  style={{ fontSize: 13, color: primaryColor, textDecoration: 'none' }}
                  onClick={(e) => {
                    e.preventDefault();
                    message.info('Vui lòng liên hệ quản trị viên để đặt lại mật khẩu.');
                  }}
                >
                  Quên mật khẩu?
                </a>
              </div>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loginMutation.isPending}
                size="large"
                block
                style={{
                  height: 48,
                  fontSize: 15,
                  fontWeight: 600,
                  background: primaryDark,
                  borderColor: primaryDark,
                  borderRadius: 6,
                  letterSpacing: 0.5,
                }}
              >
                Đăng nhập
              </Button>
            </Form.Item>
          </Form>

          {/* Mobile: hiển thị địa chỉ + hotline ở footer */}
          {!showBrandColumn && (
            <div style={{
              marginTop: 24, paddingTop: 16,
              borderTop: '1px solid #eceef1',
              fontSize: 11, color: '#7d8a98',
              textAlign: 'center', lineHeight: 1.8,
            }}>
              <div>📍 {companyAddress}</div>
              <div>📞 Hotline: {companyHotline}</div>
            </div>
          )}

          {/* Copyright */}
          <div style={{
            marginTop: 32, textAlign: 'center',
            fontSize: 11, color: '#a5b0c0', lineHeight: 1.6,
          }}>
            {copyrightText}
          </div>
        </div>
      </div>
    </div>
  );
}
